import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import {
    Canvas,
    FabricImage,
    Rect,
    Circle,
    Ellipse,
    Triangle,
    Polygon,
    Line,
    IText,
    PencilBrush,
} from "fabric";

// ═══════════════════════════════════════════════════════════════
// DEFAULT COLOR PALETTE
// ═══════════════════════════════════════════════════════════════
const COLOR_PALETTE = [
    "#000000", "#4b5563", "#6b7280", "#9ca3af", "#d1d5db", "#e5e7eb", "#ffffff",
    "#ef4444", "#f87171", "#ec4899", "#d8b4fe", "#a78bfa", "#7c3aed", "#4338ca",
    "#0891b2", "#06b6d4", "#22d3ee", "#38bdf8", "#3b82f6", "#1e40af", "#1e3a5f",
    "#10b981", "#84cc16", "#d9f99d", "#facc15", "#f97316", "#ea580c", "#dc2626",
];

// ═══════════════════════════════════════════════════════════════
// SHAPE HELPERS
// ═══════════════════════════════════════════════════════════════
function starPoints(outerR, innerR, n) {
    const pts = [];
    for (let i = 0; i < n * 2; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const a = (Math.PI / n) * i - Math.PI / 2;
        pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
    }
    return pts;
}

function hexPoints(r) {
    const pts = [];
    for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
    }
    return pts;
}

function diamondPoints(w, h) {
    return [{ x: 0, y: -h }, { x: w, y: 0 }, { x: 0, y: h }, { x: -w, y: 0 }];
}

function arrowPoints() {
    return [
        { x: 0, y: 20 }, { x: 60, y: 20 }, { x: 60, y: 0 },
        { x: 90, y: 30 }, { x: 60, y: 60 }, { x: 60, y: 40 }, { x: 0, y: 40 },
    ];
}

const SHAPES = [
    { key: "rect", label: "Rectangle", icon: "▭" },
    { key: "circle", label: "Circle", icon: "○" },
    { key: "ellipse", label: "Ellipse", icon: "⬮" },
    { key: "triangle", label: "Triangle", icon: "△" },
    { key: "star", label: "Star", icon: "☆" },
    { key: "hexagon", label: "Hexagon", icon: "⬡" },
    { key: "diamond", label: "Diamond", icon: "◇" },
    { key: "arrow", label: "Arrow", icon: "➤" },
    { key: "line", label: "Line", icon: "╱" },
];

function createShape(key, x, y, stroke, strokeW, fill) {
    const common = { stroke, strokeWidth: strokeW, strokeUniform: true };
    switch (key) {
        case "rect":
            return new Rect({ left: x - 50, top: y - 40, width: 100, height: 80, fill, ...common });
        case "circle":
            return new Circle({ left: x - 40, top: y - 40, radius: 40, fill, ...common });
        case "ellipse":
            return new Ellipse({ left: x - 50, top: y - 30, rx: 50, ry: 30, fill, ...common });
        case "triangle":
            return new Triangle({ left: x - 40, top: y - 45, width: 80, height: 90, fill, ...common });
        case "star":
            return new Polygon(starPoints(40, 18, 5), { left: x - 40, top: y - 40, fill, ...common });
        case "hexagon":
            return new Polygon(hexPoints(40), { left: x - 40, top: y - 40, fill, ...common });
        case "diamond":
            return new Polygon(diamondPoints(40, 55), { left: x - 40, top: y - 55, fill, ...common });
        case "arrow":
            return new Polygon(arrowPoints(), { left: x - 45, top: y - 30, fill, ...common });
        case "line":
            return new Line([x - 50, y, x + 50, y], { stroke, strokeWidth: strokeW });
        default:
            return null;
    }
}

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════
const I = {
    Select: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /><path d="M13 13l6 6" />
        </svg>
    ),
    Pencil: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
        </svg>
    ),
    Shapes: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="8" height="8" rx="1" /><circle cx="17.5" cy="7" r="4" /><path d="M8 21l-5-8h10z" /><rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
    ),
    Text: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" />
        </svg>
    ),
    Undo: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
        </svg>
    ),
    Redo: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.13-9.36L23 10" />
        </svg>
    ),
    Export: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
        </svg>
    ),
    ChevDown: () => (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
        </svg>
    ),
};

// ═══════════════════════════════════════════════════════════════
// TOOLBAR BUTTON
// ═══════════════════════════════════════════════════════════════
function ToolBtn({ icon: Icon, active, onClick, tooltip, disabled, children, style: extraStyle }) {
    const [hov, setHov] = useState(false);
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            disabled={disabled}
            title={tooltip}
            style={{
                height: 34, display: "flex", alignItems: "center", justifyContent: "center",
                gap: 4, border: "none", borderRadius: "6px",
                cursor: disabled ? "default" : "pointer",
                background: active ? "#ede9fe" : hov ? "#f5f5f5" : "transparent",
                color: active ? "#7c3aed" : disabled ? "#d1d5db" : "#4b5563",
                transition: "all 0.12s", opacity: disabled ? 0.5 : 1,
                padding: children ? "0 10px" : "0 8px",
                fontSize: "12px", fontWeight: 500, fontFamily: "inherit", whiteSpace: "nowrap",
                ...extraStyle,
            }}
        >
            {Icon && <Icon />}
            {children}
        </button>
    );
}

function Sep() {
    return <div style={{ width: 1, height: 24, background: "#e5e7eb", margin: "0 4px" }} />;
}

// ═══════════════════════════════════════════════════════════════
// COLOR PICKER WITH PALETTE
// ═══════════════════════════════════════════════════════════════
function ColorPicker({ color, onChange, label }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const nativeRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                onClick={() => setOpen((p) => !p)}
                title={label}
                style={{
                    width: 28, height: 28, borderRadius: "6px",
                    border: "2px solid #e5e7eb", background: color,
                    cursor: "pointer", padding: 0, display: "block",
                }}
            />
            {open && (
                <div style={dropStyles.palette}>
                    <div style={dropStyles.grid}>
                        {COLOR_PALETTE.map((c) => (
                            <button
                                key={c}
                                onClick={() => { onChange(c); setOpen(false); }}
                                style={{
                                    width: 26, height: 26, borderRadius: "50%",
                                    border: c === color ? "2px solid #7c3aed" : c === "#ffffff" ? "1px solid #d1d5db" : "2px solid transparent",
                                    background: c, cursor: "pointer", padding: 0, transition: "transform 0.1s",
                                    boxShadow: c === color ? "0 0 0 2px rgba(124,58,237,0.25)" : "none",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                            />
                        ))}
                    </div>
                    <div style={{ height: 1, background: "#e5e7eb", margin: "6px 0" }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Custom</span>
                        <div
                            onClick={() => nativeRef.current?.click()}
                            style={{
                                width: 28, height: 28, borderRadius: "6px",
                                border: "2px dashed #d1d5db",
                                background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
                                cursor: "pointer", position: "relative", overflow: "hidden",
                            }}
                        >
                            <input
                                ref={nativeRef} type="color" value={color}
                                onChange={(e) => { onChange(e.target.value); setOpen(false); }}
                                style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
                            />
                        </div>
                        <span style={{ fontSize: "11px", color: "#9ca3af", fontFamily: "monospace" }}>{color}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

const dropStyles = {
    palette: {
        position: "absolute", top: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: "10px",
        padding: "10px", boxShadow: "0 8px 30px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 210,
    },
    grid: {
        display: "grid", gridTemplateColumns: "repeat(7, 26px)", gap: "6px", justifyContent: "center",
    },
};

// ═══════════════════════════════════════════════════════════════
// SHAPES DROPDOWN
// ═══════════════════════════════════════════════════════════════
function ShapesDropdown({ onSelect, activeShape }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const isShapeActive = SHAPES.some((s) => s.key === activeShape);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <ToolBtn icon={I.Shapes} active={isShapeActive} onClick={() => setOpen((p) => !p)} tooltip="Shapes">
                <span>Shapes</span><I.ChevDown />
            </ToolBtn>
            {open && (
                <div style={shapeDropStyles.menu}>
                    {SHAPES.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => { onSelect(s.key); setOpen(false); }}
                            style={{
                                display: "flex", alignItems: "center", gap: 10, width: "100%",
                                padding: "7px 12px", border: "none", borderRadius: "6px",
                                background: activeShape === s.key ? "#ede9fe" : "transparent",
                                color: activeShape === s.key ? "#7c3aed" : "#374151",
                                cursor: "pointer", fontSize: "13px", fontFamily: "inherit", fontWeight: 500, textAlign: "left",
                            }}
                            onMouseEnter={(e) => { if (activeShape !== s.key) e.currentTarget.style.background = "#f5f5f5"; }}
                            onMouseLeave={(e) => { if (activeShape !== s.key) e.currentTarget.style.background = "transparent"; }}
                        >
                            <span style={{ fontSize: "18px", width: 24, textAlign: "center", lineHeight: 1 }}>{s.icon}</span>
                            <span>{s.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const shapeDropStyles = {
    menu: {
        position: "absolute", top: "calc(100% + 6px)", left: 0,
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: "10px",
        padding: "6px", boxShadow: "0 8px 30px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 160,
    },
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const FabricImageEditor = forwardRef(function FabricImageEditor({ src, name }, ref) {
    const canvasRef = useRef(null);
    const fabricRef = useRef(null);
    const containerRef = useRef(null);

    // ── Expose getExportedDataUrl to parent via ref ──
    useImperativeHandle(ref, () => ({
        getExportedDataUrl: () => {
            const fc = fabricRef.current;
            if (!fc) return null;
            fc.discardActiveObject();
            fc.renderAll();
            const bg = fc.getObjects().find(o => o._isBgImage);
            if (!bg) return fc.toDataURL({ format: "png", multiplier: 1 });
            const cropW = bg.width * bg.scaleX;
            const cropH = bg.height * bg.scaleY;
            return fc.toDataURL({
                format: "png",
                multiplier: 1 / bg.scaleX,
                left: 0,
                top: 0,
                width: cropW,
                height: cropH,
            });
        },
    }), []);

    const [tool, setTool] = useState("select");
    const [strokeColor, setStrokeColor] = useState("#ef4444");
    const [fillColor, setFillColor] = useState("transparent");
    const [strokeWidth, setStrokeWidth] = useState(3);
    const [fillEnabled, setFillEnabled] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const historyRef = useRef([]);
    const historyIndexRef = useRef(-1);
    const isRestoringRef = useRef(false);

    const saveHistory = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc || isRestoringRef.current) return;
        const json = JSON.stringify(fc.toJSON());
        historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
        historyRef.current.push(json);
        historyIndexRef.current = historyRef.current.length - 1;
        setCanUndo(historyIndexRef.current > 0);
        setCanRedo(false);
    }, []);

    const restoreHistory = useCallback((index) => {
        const fc = fabricRef.current;
        if (!fc || !historyRef.current[index]) return;
        isRestoringRef.current = true;
        fc.loadFromJSON(historyRef.current[index]).then(() => {
            fc.renderAll();
            const bg = fc.getObjects().find((o) => o._isBgImage);
            if (bg) { bg.selectable = false; bg.evented = false; }
            isRestoringRef.current = false;
            historyIndexRef.current = index;
            setCanUndo(index > 0);
            setCanRedo(index < historyRef.current.length - 1);
        });
    }, []);

    const handleUndo = useCallback(() => {
        if (historyIndexRef.current > 0) restoreHistory(historyIndexRef.current - 1);
    }, [restoreHistory]);

    const handleRedo = useCallback(() => {
        if (historyIndexRef.current < historyRef.current.length - 1) restoreHistory(historyIndexRef.current + 1);
    }, [restoreHistory]);

    // ── Init canvas ──
    useEffect(() => {
        const c = new Canvas(canvasRef.current, { backgroundColor: "#f9fafb", selection: true });
        const container = containerRef.current;
        if (container) c.setDimensions({ width: container.offsetWidth, height: container.offsetHeight });
        fabricRef.current = c;

        ["object:added", "object:modified", "object:removed"].forEach((e) => c.on(e, () => saveHistory()));

        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "z") { e.preventDefault(); e.shiftKey ? handleRedo() : handleUndo(); }
            if (e.key === "Delete" || e.key === "Backspace") {
                const active = c.getActiveObject();
                if (active && !active._isBgImage && !(active instanceof IText && active.isEditing)) {
                    c.remove(active); c.discardActiveObject(); c.renderAll();
                }
            }
        };
        window.addEventListener("keydown", onKey);

        return () => { window.removeEventListener("keydown", onKey); c.dispose(); };
    }, [saveHistory, handleUndo, handleRedo]);

    // ── Auto-load image from src prop ──
    useEffect(() => {
        if (!src) return;
        const fc = fabricRef.current;
        if (!fc) return;

        const timer = setTimeout(() => {
            const imgEl = new Image();
            imgEl.onload = () => {
                fc.clear();
                fc.backgroundColor = "#f9fafb";
                historyRef.current = [];
                historyIndexRef.current = -1;

                const container = containerRef.current;
                const maxW = container ? container.offsetWidth : 800;
                const maxH = container ? container.offsetHeight : 600;
                const scale = Math.min(maxW / imgEl.width, maxH / imgEl.height, 1);
                const finalW = imgEl.width * scale;
                const finalH = imgEl.height * scale;
                fc.setDimensions({ width: finalW, height: finalH });

                const fabricImg = new FabricImage(imgEl, {
                    scaleX: scale,
                    scaleY: scale,
                    selectable: false,
                    evented: false,
                    originX: "left",
                    originY: "top",
                    left: 0,
                    top: 0,
                });
                fabricImg._isBgImage = true;
                fc.add(fabricImg);
                fc.renderAll();
                saveHistory();
            };
            imgEl.src = src;
        }, 50);

        return () => clearTimeout(timer);
    }, [src, saveHistory]);

    // ── Tool mode ──
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        if (tool === "draw") {
            fc.isDrawingMode = true;
            const brush = new PencilBrush(fc);
            brush.color = strokeColor;
            brush.width = strokeWidth;
            fc.freeDrawingBrush = brush;
            fc.selection = false;
        } else {
            fc.isDrawingMode = false;
            fc.selection = tool === "select";
        }
    }, [tool, strokeColor, strokeWidth]);

    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc || !fc.freeDrawingBrush) return;
        fc.freeDrawingBrush.color = strokeColor;
        fc.freeDrawingBrush.width = strokeWidth;
    }, [strokeColor, strokeWidth]);

    // ── Canvas click for shape/text placement ──
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;

        const onMouseDown = (opt) => {
            if (tool === "select" || tool === "draw") return;
            const pointer = fc.getScenePoint(opt.e);
            const fill = fillEnabled ? fillColor : "transparent";

            if (tool === "text") {
                const text = new IText("Type here", {
                    left: pointer.x - 40, top: pointer.y - 12,
                    fontSize: 22, fill: strokeColor,
                    fontFamily: "Arial, sans-serif", editable: true,
                });
                fc.add(text);
                fc.setActiveObject(text);
                fc.renderAll();
                setTool("select");
                return;
            }

            const shape = createShape(tool, pointer.x, pointer.y, strokeColor, strokeWidth, fill);
            if (shape) {
                fc.add(shape);
                fc.setActiveObject(shape);
                fc.renderAll();
                setTool("select");
            }
        };

        fc.on("mouse:down", onMouseDown);
        return () => fc.off("mouse:down", onMouseDown);
    }, [tool, strokeColor, fillColor, fillEnabled, strokeWidth]);

    // ── Export as download ──
    const handleExport = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        fc.discardActiveObject();
        fc.renderAll();
        const bg = fc.getObjects().find(o => o._isBgImage);
        const cropW = bg ? bg.width * bg.scaleX : fc.getWidth();
        const cropH = bg ? bg.height * bg.scaleY : fc.getHeight();
        const mult = bg ? 1 / bg.scaleX : 2;
        const dataUrl = fc.toDataURL({ format: "png", multiplier: mult, left: 0, top: 0, width: cropW, height: cropH });
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `edited_${Date.now()}.png`;
        a.click();
    }, []);

    const isShapeTool = SHAPES.some((s) => s.key === tool);

    return (
        <div style={styles.root}>
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

            {/* ─── TOOLBAR ─── */}
            <div style={styles.toolbar}>
                <div style={styles.toolGroup}>
                    <ToolBtn icon={I.Select} tooltip="Select" active={tool === "select"} onClick={() => setTool("select")} />
                    <ToolBtn icon={I.Pencil} tooltip="Draw" active={tool === "draw"} onClick={() => setTool("draw")} />
                    <Sep />
                    <ShapesDropdown activeShape={isShapeTool ? tool : null} onSelect={(key) => setTool(key)} />
                    <Sep />
                    <ToolBtn icon={I.Text} tooltip="Text Box" active={tool === "text"} onClick={() => setTool("text")}>
                        <span>Text Box</span>
                    </ToolBtn>
                </div>

                <div style={styles.toolGroup}>
                    <span style={styles.label}>Stroke</span>
                    <ColorPicker color={strokeColor} onChange={setStrokeColor} label="Stroke Color" />

                    <button
                        onClick={() => { setFillEnabled((p) => !p); if (!fillEnabled && fillColor === "transparent") setFillColor("#3b82f6"); }}
                        title={fillEnabled ? "Fill ON" : "Fill OFF"}
                        style={{
                            height: 28, display: "flex", alignItems: "center", gap: 4,
                            padding: "0 8px", borderRadius: "6px",
                            border: fillEnabled ? "1.5px solid #7c3aed" : "1.5px dashed #d1d5db",
                            background: fillEnabled ? "#ede9fe" : "transparent",
                            color: fillEnabled ? "#7c3aed" : "#9ca3af",
                            cursor: "pointer", fontSize: "11px", fontWeight: 600, fontFamily: "inherit", marginLeft: 4,
                        }}
                    >
                        Fill {fillEnabled ? "ON" : "OFF"}
                    </button>

                    {fillEnabled && (
                        <ColorPicker color={fillColor} onChange={setFillColor} label="Fill Color" />
                    )}

                    <Sep />
                    <span style={styles.label}>{strokeWidth}px</span>
                    <input
                        type="range" min={1} max={20} value={strokeWidth}
                        onChange={(e) => setStrokeWidth(Number(e.target.value))}
                        style={{ width: 70, accentColor: "#7c3aed", cursor: "pointer" }}
                    />
                </div>

                <div style={styles.toolGroup}>
                    <ToolBtn icon={I.Undo} tooltip="Undo (Ctrl+Z)" onClick={handleUndo} disabled={!canUndo} />
                    <ToolBtn icon={I.Redo} tooltip="Redo (Ctrl+Shift+Z)" onClick={handleRedo} disabled={!canRedo} />
                    <Sep />
                    <ToolBtn icon={I.Export} tooltip="Export PNG" onClick={handleExport} />
                </div>
            </div>

            {/* ─── CANVAS AREA ─── */}
            <div ref={containerRef} style={styles.canvasContainer}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
});

export default FabricImageEditor;

// ═══════════════════════════════════════════════════════════════
const styles = {
    root: {
        width: "100%", height: "100%", display: "flex", flexDirection: "column",
        background: "#ffffff", fontFamily: "'DM Sans', -apple-system, sans-serif", overflow: "hidden",
    },
    toolbar: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "5px 12px", borderBottom: "1px solid #e5e7eb", background: "#fff",
        flexShrink: 0, gap: "8px", flexWrap: "wrap", minHeight: 46,
    },
    toolGroup: { display: "flex", alignItems: "center", gap: "4px" },
    label: { fontSize: "11px", color: "#6b7280", fontWeight: 500 },
    canvasContainer: {
        flex: 1, position: "relative", overflow: "hidden",
        background: "#e8e8e8",
        display: "flex", alignItems: "center", justifyContent: "center",
    },
};